// fixed_header: a thread header's arrow is BACK, like every app's: it slides
// to the panel on the left (the strip is history), the same place a swipe
// right or the left edge chevron goes. Landed on directly, with nothing on the
// left, it goes UP instead, opening the crumb before the thread there: the
// parent thread, or the channel for a root thread. Up otherwise lives in the
// header's crumbs and the ancestor rows above the head post.

type FilterLike = Record<string, any> | null | undefined

export type UpTarget = {
    // What to open: the parent thread's filter, or the channel's stream filter.
    filter: Record<string, any>
    // Who it is, for the tooltip: "Back to <label>".
    label: string
}

// Panel filters carry UI-only keys alongside the stream identity.
const NON_IDENTITY_KEYS = new Set(['answerId', 'order', 'page', 'depth'])

function streamIdentity(filter: FilterLike): string {
    if (!filter) return ''
    const entries = Object.entries(filter)
        .filter(([key, value]) => !NON_IDENTITY_KEYS.has(key) && value != null && value !== false)
        .map(([key, value]) => [key, Array.isArray(value) ? value.map(String).sort() : value])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
    return JSON.stringify(entries)
}

export type BackAction = { kind: 'slide' } | { kind: 'open'; target: UpTarget }

type PanelLike = { filter?: FilterLike; empty?: boolean } | null | undefined

// What the arrow does: back to the panel on the left when there is one,
// otherwise up to the thread's parent (or nothing, with no parent either).
export function resolveBackAction(left: PanelLike, up: UpTarget | null): BackAction | null {
    if (left && !left.empty) return { kind: 'slide' }
    return up ? { kind: 'open', target: up } : null
}

// Whether a panel already shows the arrow's target.
export function panelShowsTarget(panelFilter: FilterLike, target: UpTarget | null): boolean {
    if (!panelFilter || !target) return false
    if (target.filter.questId) {
        return String(panelFilter.questId || '') === String(target.filter.questId)
    }
    if (panelFilter.questId) return false
    return streamIdentity(panelFilter) === streamIdentity(target.filter)
}
